import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from 'vue';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'vue-chartjs';
import { MawbMrnApi, MawbMrnModel, StringStringKeyValueModel } from '@/openapi';
import { useRouter, useRoute } from 'vue-router/composables';
import {
    downloadFile,
    getFileNameFromContentDispositionHeader,
} from '@/helpers/downloadHelper';
enum SourceType {
    MAWB,
    MRN,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MrnMawb',
  setup(__props) {

const router = useRouter();
const route = useRoute();

ChartJS.register(ArcElement, Tooltip, Legend);
const api = new MawbMrnApi(undefined, '');

// Data placeholders
const mrnNumbers = ref<StringStringKeyValueModel[]>([]);
const mawbNumbers = ref<StringStringKeyValueModel[]>([]);
const shipments = ref<MawbMrnModel[]>([]);
const loading = ref(false);
const downloading = ref(false);

const selectedMrn = ref('');
const selectedMawb = ref('');
const footerProps = ref({
    'items-per-page-options': [100, 250, 500],
});
const itemsPerPage = ref(100);

const selectedNumbers = computed(() => {
    if (selectedMrn.value) {
        return selectedMrn.value;
    } else {
        return selectedMawb.value;
    }
});

// Metrics
const clearedShipments = computed(
    () => shipments.value.filter((s) => s.finalCleared).length
);
const preClearedShipments = computed(
    () => shipments.value.filter((s) => s.preCleared).length
);
const waitingClearanceShipments = computed(
    () => shipments.value.filter((s) => s.waitingForCustomsClearance).length
);
const waitingPoAShipments = computed(
    () => shipments.value.filter((s) => s.waitingForPoA).length
);

const documentCustomsInspection = computed(
    () => shipments.value.filter((s) => s.documentCustomsInspection).length
);
const physicalCustomsInspection = computed(
    () => shipments.value.filter((s) => s.physicalCustomsInspection).length
);
const pendingForPaymentDapOnly = computed(
    () => shipments.value.filter((s) => s.pendingForPaymentDapOnly).length
);
const missing = computed(() => shipments.value.filter((s) => s.missing).length);
const destroyedConficatedByCustoms = computed(
    () => shipments.value.filter((s) => s.destroyedConficatedByCustoms).length
);
const returnShipments = computed(
    () => shipments.value.filter((s) => s.returnShipments).length
);

const problemShipments = computed(
    () => shipments.value.filter((s) => s.isProblemShipment).length
);

// Percentages
const totalShipments = computed(() => shipments.value.length);
const clearedPercentage = computed(() =>
    calculatePercentage(clearedShipments.value, totalShipments.value)
);
const preClearedPercentage = computed(() =>
    calculatePercentage(preClearedShipments.value, totalShipments.value)
);
const waitingClearancePercentage = computed(() =>
    calculatePercentage(waitingClearanceShipments.value, totalShipments.value)
);
const waitingPoAPercentage = computed(() =>
    calculatePercentage(waitingPoAShipments.value, totalShipments.value)
);

const documentCustomsInspectionPercentage = computed(() =>
    calculatePercentage(documentCustomsInspection.value, totalShipments.value)
);
const physicalCustomsInspectionPercentage = computed(() =>
    calculatePercentage(physicalCustomsInspection.value, totalShipments.value)
);
const pendingForPaymentDapOnlyPercentage = computed(() =>
    calculatePercentage(pendingForPaymentDapOnly.value, totalShipments.value)
);
const missingPercentage = computed(() =>
    calculatePercentage(missing.value, totalShipments.value)
);
const destroyedConficatedByCustomsPercentage = computed(() =>
    calculatePercentage(
        destroyedConficatedByCustoms.value,
        totalShipments.value
    )
);
const returnShipmentsPercentage = computed(() =>
    calculatePercentage(returnShipments.value, totalShipments.value)
);

const problemPercentage = computed(() =>
    calculatePercentage(problemShipments.value, totalShipments.value)
);

// Chart Data
const chartData = computed(() => ({
    labels: [
        'Cleared Shipments',
        'Pre-cleared Shipments',
        'Waiting for Customs Clearance',
        'Waiting for PoA',
        'Problem Shipments',
    ],
    datasets: [
        {
            data: [
                clearedShipments.value,
                preClearedShipments.value,
                waitingClearanceShipments.value,
                waitingPoAShipments.value,
                problemShipments.value,
            ],
            backgroundColor: [
                '#4caf50',
                '#ffeb3b',
                '#03a9f4',
                '#ff9800',
                '#f44336',
            ],
        },
    ],
}));

function calculatePercentage(part: number, total: number) {
    return total === 0 ? '0.00' : ((part / total) * 100).toFixed(2);
}

function getDropdown() {
    loading.value = true;
    api.getMawbsAndMrns()
        .then((response) => {
            mrnNumbers.value = response.data.mrnList ?? [];
            mawbNumbers.value = response.data.mawbList ?? [];
        })
        .finally(() => {
            loading.value = false;
        });
}

function downloadShipment() {
    downloading.value = true;
    api.downloadMawbMrnShipments(
        selectedMawb.value ?? '',
        selectedMrn.value ?? '',
        {
            responseType: 'blob',
        }
    ).then((response) => {
        const fileName = getFileNameFromContentDispositionHeader(response);
        downloading.value = false;
        if (!fileName) {
            downloadFile(
                response.data,
                `MawbMrn_${new Date().toISOString()}.xlsx`
            );
            return;
        }
        downloadFile(response.data, fileName!);
    });
}

function getItems() {
    if (!selectedMawb.value && !selectedMrn.value) {
        return;
    }
    loading.value = true;
    api.getMawbMrnShipments(selectedMawb.value ?? '', selectedMrn.value ?? '')
        .then((response) => {
            shipments.value = response.data;
        })
        .finally(() => {
            loading.value = false;
        });
}

const toggleShipmentSource = (sourceType: SourceType) => {
    if (sourceType === SourceType.MAWB) {
        if (selectedMrn.value) {
            selectedMrn.value = '';
        }
    } else {
        if (selectedMawb.value) {
            selectedMawb.value = '';
        }
    }

    getItems();
};

onMounted(() => {
    getDropdown();
});

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        tooltip: {
            callbacks: {
                label: function(context: any) {
                    const totalCount: number = context.dataset.data.reduce(
                        (partialSum: number, a: number) => partialSum + a,
                        0
                    );
                    const current: number = context.raw;
                    const result = ((current / totalCount) * 100).toFixed(2);
                    if (result.endsWith('.00')) {
                        return `${current} (${result.slice(0, -3)}%)`;
                    }
                    return `${current} (${result}%)`;
                },
            },
        },
    },
};

const tableHeaders = [
    { text: 'HAWB', value: 'hawb' },
    { text: 'MAWB', value: 'mawb' },
    { text: 'Sender', value: 'sender' },
    { text: 'MRN', value: 'mrn' },
    { text: 'Waiting for PoA', value: 'waitingForPoA' },
    {
        text: 'Waiting for Customs Clearance',
        value: 'waitingForCustomsClearance',
    },
    { text: 'Pre Cleared', value: 'preCleared' },
    { text: 'Final Cleared', value: 'finalCleared' },
];

return { __sfc: true,router, route, SourceType, api, mrnNumbers, mawbNumbers, shipments, loading, downloading, selectedMrn, selectedMawb, footerProps, itemsPerPage, selectedNumbers, clearedShipments, preClearedShipments, waitingClearanceShipments, waitingPoAShipments, documentCustomsInspection, physicalCustomsInspection, pendingForPaymentDapOnly, missing, destroyedConficatedByCustoms, returnShipments, problemShipments, totalShipments, clearedPercentage, preClearedPercentage, waitingClearancePercentage, waitingPoAPercentage, documentCustomsInspectionPercentage, physicalCustomsInspectionPercentage, pendingForPaymentDapOnlyPercentage, missingPercentage, destroyedConficatedByCustomsPercentage, returnShipmentsPercentage, problemPercentage, chartData, calculatePercentage, getDropdown, downloadShipment, getItems, toggleShipmentSource, chartOptions, tableHeaders, Pie }
}

})